import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

/**
 * Decode a JWT payload WITHOUT verifying its signature.
 * Frontend port of the backend `decodeJwtPayload` (jwt-claims.util.ts).
 */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const decoded = jwtDecode<Record<string, unknown>>(token);
    return decoded && typeof decoded === "object" ? decoded : null;
  } catch {
    return null;
  }
};

/**
 * Extract claim keys from a JWT into a string map.
 * Frontend port of the backend `claimExactKeys` (jwt-claims.util.ts): object/nullish
 * claims are skipped, everything else is coerced to string.
 *
 * @example
 * claimExactKeys(accessToken, ["sub", "matchId"]) // => { sub: "...", matchId: "..." }
 */
export const claimExactKeys = <K extends string>(token: string, keys: readonly K[]): Partial<Record<K, string>> => {
  const payload = decodeJwtPayload(token);
  if (!payload) return {};

  const claimed: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = payload[key];
    if (value == null || typeof value === "object") continue;
    claimed[key] = String(value);
  }
  return claimed;
};

export type TSessionIdentity = {
  sub?: string;
  email?: string;
  /** 組織ID (`A12-3456-7890`) — the id the user logs in with on the center. */
  externalId?: string;
  matchId?: string;
  matchLicenseNumber?: string;
};

const SESSION_IDENTITY_CLAIM_KEYS = ["sub", "email", "externalId", "matchId", "matchLicenseNumber"] as const;

/**
 * Identity of the current session, read from the access token claims instead of
 * from dedicated cookies — the token is the single source of truth, so the email
 * / 組織ID can never drift from the session they belong to (e.g. after the center
 * OAuth handoff or a login in another tab).
 *
 * Since the center mints the access token this app stores, its claims carry the
 * same `email` / `externalId` the center reads.
 *
 * The cookie is read directly (instead of through `@/utils/token`) to stay out of
 * the Axios import cycle.
 *
 * Callers that clear the session must read this BEFORE clearing it.
 */
export const getSessionIdentity = (): TSessionIdentity => {
  const accessToken = Cookies.get("match_token");
  if (!accessToken) return {};

  return claimExactKeys(accessToken, SESSION_IDENTITY_CLAIM_KEYS);
};

export const hasIdentity = (identity?: TSessionIdentity | null): identity is TSessionIdentity =>
  !!identity && Object.values(identity).some(Boolean);
